import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  List,
  X,
  Phone,
  CaretDown,
  InstagramLogo,
  FacebookLogo,
  GoogleLogo,
} from "@phosphor-icons/react";
import useLightTone from "@/hooks/useLightTone";

const SERVICE_LINKS = [
  { label: "Website design, hosting & management", note: "$499 build + $79/mo", href: "#pricing", id: "websites" },
  { label: "Improve an existing website", note: "Free review", href: "/website-review", id: "improve" },
  { label: "Google Business Profile setup", note: "$299 one-time", href: "/google-business-profile", id: "google-profile" },
  { label: "Local Visibility", note: "$149 + $299/mo", href: "/local-visibility", id: "local-visibility" },
  { label: "Quote & call automations", note: "Quoted", href: "#automations", id: "automations" },
];

const links = [
  { label: "Work", href: "#work" },
  { label: "Services", href: "#services", menu: true },
  { label: "Pricing", href: "#pricing" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];
const PLAIN_LINKS = links.filter((l) => !l.menu);
const MOBILE_PRIMARY_LINKS = PLAIN_LINKS.slice(0, 1);
const MOBILE_SECONDARY_LINKS = PLAIN_LINKS.slice(1);

const socialLinks = [
  { label: "Instagram", href: "https://www.instagram.com/webjesters/", Icon: InstagramLogo },
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61590487411718", Icon: FacebookLogo },
  { label: "Reviews", href: "https://share.google/xWSzLXVo1ST2fSjyU", Icon: GoogleLogo },
];

function NavItem({ href, children, ...rest }) {
  return href.startsWith("#") ? (
    <a href={href} {...rest}>{children}</a>
  ) : (
    <Link to={href} {...rest}>{children}</Link>
  );
}

function ServicesMenu({ href, onNavigate, light }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const show = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true });
    return () => window.removeEventListener("scroll", close);
  }, [open]);

  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        data-testid="nav-link-services"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 text-[14px] transition-colors whitespace-nowrap ${light ? "text-zinc-600 hover:text-zinc-900" : "text-zinc-400 hover:text-white"}`}
      >
        Services
        <CaretDown size={11} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-services-menu"
            role="menu"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18 }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[340px]"
          >
            <div className="rounded-2xl border border-white/10 bg-[#0e0e10]/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] p-2">
              {SERVICE_LINKS.map((s) => (
                <NavItem
                  key={s.id}
                  href={href(s.href)}
                  role="menuitem"
                  data-testid={`nav-service-${s.id}`}
                  onClick={() => { setOpen(false); onNavigate?.(); }}
                  className="flex items-center justify-between gap-4 px-3.5 py-2.5 rounded-xl hover:bg-white/[0.05] transition-colors"
                >
                  <span className="text-[13.5px] text-zinc-200">{s.label}</span>
                  <span className="text-[12px] text-zinc-500 whitespace-nowrap">{s.note}</span>
                </NavItem>
              ))}
              <a
                href={href("#services")}
                onClick={() => setOpen(false)}
                data-testid="nav-service-all"
                className="mt-1 block px-3.5 py-2.5 text-[12.5px] text-zinc-400 hover:text-white border-t border-white/[0.06]"
              >
                See all services →
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const probeBelowBar = () => (window.innerWidth >= 768 ? 84 : 68);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  const href = (h) => (h.startsWith("#") && !onHome ? `/${h}` : h);
  const overLight = useLightTone(probeBelowBar);
  const light = scrolled && overLight && !open;
  const linkCls = light ? "text-zinc-600 hover:text-zinc-900" : "text-zinc-400 hover:text-white";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    window.dispatchEvent(new CustomEvent("webjesters:menu", { detail: open }));
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-navbar"
        data-light={light ? "true" : "false"}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-300 ${
          !scrolled
            ? "bg-transparent border-b border-transparent"
            : light
              ? "bg-[#f6f5f1]/85 backdrop-blur-xl border-b border-zinc-200"
              : "glass border-b border-white/[0.06]"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group" onClick={() => { setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
            <img
              src={light ? "/logo-horizontal.png" : "/logo-horizontal-white.png"}
              alt="WebJesters"
              width="1600"
              height="333"
              className="h-7 md:h-8 w-auto transition-opacity group-hover:opacity-90"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) =>
              l.menu ? (
                <ServicesMenu key={l.href} href={href} light={light} />
              ) : (
                <NavItem
                  key={l.href}
                  href={href(l.href)}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-[14px] transition-colors whitespace-nowrap ${linkCls}`}
                >
                  {l.label}
                </NavItem>
              )
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:6023905623"
              data-testid="navbar-phone"
              className={`text-[13px] transition-colors flex items-center gap-2 ${linkCls}`}
            >
              <Phone size={15} weight="regular" />
              602-390-5623
            </a>
            <a
              href={href("#contact")}
              data-testid="navbar-cta-quote"
              className={`inline-flex items-center px-5 h-10 rounded-full text-[13px] font-medium transition-colors ${
                light ? "bg-zinc-900 text-white hover:bg-brand-700" : "bg-white text-black hover:bg-zinc-200"
              }`}
            >
              Get a quote
            </a>
          </div>

          <button
            type="button"
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
            className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border transition-colors ${
              light ? "border-zinc-300 bg-white text-zinc-900" : "border-white/10 bg-white/[0.03] text-white"
            }`}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden bg-[#0a0a0a]/95 backdrop-blur-xl pt-20 overflow-y-auto"
          >
            <div className="px-6 py-6 flex flex-col">
              {MOBILE_PRIMARY_LINKS.map((l) => (
                <MobileLink key={l.href} href={href(l.href)} label={l.label} onClick={() => setOpen(false)} delay={0.05} />
              ))}

              <div className="pt-5 pb-2 text-[11px] tracking-[0.22em] uppercase text-zinc-400">Services</div>
              <div className="grid gap-1">
                {SERVICE_LINKS.map((s, i) => (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + i * 0.04 }}
                  >
                    <NavItem
                      href={href(s.href)}
                      data-testid={`mobile-nav-service-${s.id}`}
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-between gap-4 py-3 border-b border-white/[0.06]"
                    >
                      <span className="text-[16px] text-white">{s.label}</span>
                      <span className="text-[12.5px] text-zinc-400 whitespace-nowrap">{s.note}</span>
                    </NavItem>
                  </motion.div>
                ))}
              </div>

              <div className="mt-3">
                {MOBILE_SECONDARY_LINKS.map((l, i) => (
                  <MobileLink key={l.href} href={href(l.href)} label={l.label} onClick={() => setOpen(false)} delay={0.3 + i * 0.05} />
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-3">
                <a href="tel:6023905623" className="inline-flex items-center justify-center h-12 rounded-full border border-white/10 text-white">
                  <Phone size={16} weight="regular" className="mr-2" />
                  Call 602-390-5623
                </a>
                <a
                  href={href("#contact")}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta-quote"
                  className="inline-flex items-center justify-center h-12 rounded-full bg-white text-black font-medium"
                >
                  Get a quote
                </a>
              </div>

              <div className="mt-10 pt-6 pb-10 border-t border-white/[0.06]">
                <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-400 mb-4">Follow</div>
                <div className="flex items-center gap-3">
                  {socialLinks.map(({ label, href: url, Icon }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      data-testid={`mobile-social-${label.toLowerCase()}`}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/10 bg-white/[0.03] text-zinc-300 hover:text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors"
                    >
                      <Icon size={17} weight="regular" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MobileLink({ href, label, onClick, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
      <NavItem
        href={href}
        data-testid={`mobile-nav-link-${label.toLowerCase()}`}
        onClick={onClick}
        className="block font-display text-3xl text-white py-4 border-b border-white/[0.06]"
      >
        {label}
      </NavItem>
    </motion.div>
  );
}
